import { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import { Options as HighChartOptions } from 'highcharts/highcharts';

import isDefined from '../../../isDefined';
import DatasetDefinitionsContext from '../../../contexts/DatasetDefinitionsContext';
import { ChartColors } from '../../../theme/tokens';
import listToString from '../../../services/listToString';
import getSelectedSeriesName from '../getSelectedSeriesName';
import { GroupVsRemainder } from '../types';
import useGroupVsRemainder from './useGroupVsRemainder';

/**
 * Prune null values from the beginning and end of the array.
 * If null values are found after the first/last non-null value,
 * they are replaced with 0.
 */
const pruneValues = (values: number[]) => {
  const firstValueIndex = values.findIndex((v) => v !== null);
  const lastValueIndex = [...values].reverse().findIndex((v) => v !== null);

  return values.map((v, i) => {
    if (i >= firstValueIndex && i <= values.length - lastValueIndex) {
      return v || 0;
    }
    return null;
  });
};

const useGroupVsRemainderData = ({
  gadget,
}: {
  gadget: VisualisationDefinitions.GroupVsRemainder;
}): {
  options: HighChartOptions | undefined;
  hasResults: boolean;
  compatibleComparison: boolean;
} => {
  const { data, isLoading, compatibleComparison } = useGroupVsRemainder({
    groupVsRemainder: gadget,
  });
  const { datasets } = useContext(DatasetDefinitionsContext);
  const [chartOptions, setChartOptions] = useState<
    HighChartOptions | undefined
  >();
  const [hasResults, setHasResults] = useState<boolean>(false);

  useEffect(() => {
    const dataset = datasets.find((d) => d.type === gadget.dataset);
    if (isLoading || !data || !dataset) {
      setChartOptions(undefined);
      return;
    }

    const extraArgs = {
      selectedEntities: gadget.selectedEntities,
      peerGroup: gadget.peerGroup,
    };

    const prunedRemainderValues = pruneValues(data.map((d) => d.remainder));
    const remainderSeriesData: GroupVsRemainder.PointOptionsObject[] = data.map(
      (d, i) => {
        return {
          dataType: 'remainder',
          startDate: d.startDate,
          endDate: d.endDate,
          dataset: gadget.dataset,
          interval: gadget.trendByCalendarInterval,
          field: gadget.field,
          peerGroupValues: d.peerGroupValues,
          y: prunedRemainderValues[i],
          ...extraArgs,
        };
      },
    );
    const prunedGroupValues = pruneValues(data.map((d) => d.group));
    const groupSeriesData: GroupVsRemainder.PointOptionsObject[] = data.map(
      (d, i) => ({
        dataType: 'group',
        startDate: d.startDate,
        endDate: d.endDate,
        dataset: gadget.dataset,
        interval: gadget.trendByCalendarInterval,
        field: gadget.field,
        peerGroupValues: d.peerGroupValues,
        y: prunedGroupValues[i],
        ...extraArgs,
      }),
    );

    // Check if the total count between both datasets is 0
    const totalCount = data.reduce((acc, d) => acc + d.group + d.remainder, 0);
    const hasRemainderResults =
      data.reduce((acc, d) => acc + d.remainder, 0) > 0;
    const hasSelectedResults = data.reduce((acc, d) => acc + d.group, 0) > 0;

    setHasResults(totalCount > 0);
    const hasSelectedEntities = !!gadget.selectedEntities;

    let seriesName = 'Median';
    if (hasSelectedEntities && groupSeriesData[0]?.peerGroupValues) {
      const peerGroupValues = listToString(
        groupSeriesData[0].peerGroupValues,
        1,
      );
      seriesName = `Peer Group (${peerGroupValues})`;
    } else if (hasSelectedEntities) {
      seriesName = 'Remainder';
    }

    const series: Highcharts.SeriesOptionsType[] = [];
    if (gadget.selectedEntities) {
      series.push({
        // The selected entities by name, e.g. "John Smith (+2)"
        name: getSelectedSeriesName(gadget.selectedEntities),
        color: ChartColors[0],
        type: 'line',
        data: groupSeriesData,
      });
    }
    if ((hasRemainderResults && hasSelectedResults) || !hasSelectedEntities) {
      series.push({
        name: seriesName,
        type: 'line',
        color:
          hasSelectedEntities && hasRemainderResults
            ? 'orange'
            : ChartColors[0],
        dashStyle:
          hasSelectedEntities && hasRemainderResults ? 'Dash' : 'Solid',
        data: remainderSeriesData,
      });
    }

    const newChartOptions = {
      accessibility: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      xAxis: {
        tickPixelInterval:
          gadget.trendByCalendarInterval === 'hour' ? 200 : 100,
        type: 'datetime',
        padding: 11,
        labels: {
          style: {
            whiteSpace: 'nowrap',
            fontSize: 11,
          },
          formatter: function () {
            // @ts-ignore
            const i = this.value as number;
            try {
              let formatString;
              if (gadget.trendByCalendarInterval === 'hour') {
                formatString = '%m/%d %kh';
              } else if (
                ['month', 'fiscalPeriod', 'fiscalQuarter'].includes(
                  gadget.trendByCalendarInterval,
                )
              ) {
                formatString = "%m/'%y";
              } else {
                formatString = '%m/%e';
              }
              const baseMs = new Date(
                remainderSeriesData[i].startDate,
              ).getTime();
              // Weeks are labelled by their ending date (Sat) so customers can
              // read them as "week ending …" — shift forward 6 days.
              const displayMs =
                gadget.trendByCalendarInterval === 'week'
                  ? baseMs + 6 * 24 * 60 * 60 * 1000
                  : baseMs;
              return Highcharts.dateFormat(formatString, displayMs);
            } catch (ex) {
              console.warn(ex.message);
              // Prevent crash on date change
              return remainderSeriesData[i].startDate;
            }
          },
        },
        gridLineWidth: 1,
        dateTimeLabelFormats: {
          // don't display the dummy year
          month: '%m/%e',
          year: '%b',
        },
      },
      yAxis: {
        title: {
          text: undefined,
        },
        min:
          isDefined(gadget.minY) && gadget.minYBehaviour === 'set'
            ? gadget.minY
            : null,
        max:
          isDefined(gadget.maxY) && gadget.maxYBehaviour === 'set'
            ? gadget.maxY
            : null,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        shadow: false,
        borderRadius: 8,
        padding: 0,
        outside: true,
        split: false,
        borderWidth: 0,
      },
      plotOptions: {
        column: {
          pointPadding: 0,
          groupPadding: 0,
        },
      },
      legend: {
        align: 'left',
        enabled: true,
        itemStyle: {
          fontSize: '0.7em',
        },
      },
      series,
    };
    setChartOptions(newChartOptions as any as HighChartOptions);
  }, [
    data,
    datasets,
    gadget.dataset,
    gadget.field,
    gadget.maxY,
    gadget.maxYBehaviour,
    gadget.minY,
    gadget.minYBehaviour,
    gadget.peerGroup,
    gadget.selectedEntities,
    gadget.trendByCalendarInterval,
    isLoading,
  ]);

  return { options: chartOptions, hasResults, compatibleComparison };
};

export default useGroupVsRemainderData;
